import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {IS_SERVER} from '@github-ui/ssr-utils'

import {dashboardSurfaceAppBuilder} from '../config/app-builder'
import {PULLS_INBOX_PATH} from '../constants/routes'
import {isBlockingInboxSection, isDeferredInboxSection} from '../helpers/inbox-section-preferences'
import type {PullsDashboardSurfacePayload, SectionDataResult} from '../types/routes-types'

const PULLS_DASHBOARD_INBOX_ROUTE_ID = 'pullsDashboardInboxRoute'

type EmbeddedPayload = Record<string, unknown>

interface InboxSectionQueryResponse {
  payload?: {
    pullsInboxSurfaceContentRoute?: PullsDashboardSurfacePayload
  }
}

function extractSectionData(json: InboxSectionQueryResponse): SectionDataResult {
  const payload = json.payload?.pullsInboxSurfaceContentRoute
  if (!payload) {
    throw new Error('Unexpected response shape: missing pullsInboxSurfaceContentRoute')
  }
  return {results: payload.results, totalCount: payload.pageInfo.totalCount}
}

function sectionQueryDeps(sectionId: string) {
  return ({searchParams}: {searchParams: URLSearchParams}) => {
    return {
      pathname: `${PULLS_INBOX_PATH}/queries`,
      searchParams: {
        filter: `${sectionId} ${searchParams.get('filter') || ''}`.trim(),
        max_pr_age: searchParams.get('max_pr_age') || '1m',
      },
    }
  }
}

async function sectionQueryFn(queryKey: Parameters<typeof queryFnFetch>[0]): Promise<SectionDataResult> {
  const json = await queryFnFetch<InboxSectionQueryResponse>(queryKey)
  return extractSectionData(json)
}

function hasEmbeddedDataForQuery(queryName: string): boolean | undefined {
  if (!IS_SERVER) return undefined

  try {
    const embeddedPayload = dashboardSurfaceAppBuilder.getEmbeddedData()?.payload as EmbeddedPayload | undefined
    if (!embeddedPayload) return undefined

    const routePayload = embeddedPayload[PULLS_DASHBOARD_INBOX_ROUTE_ID] as EmbeddedPayload | undefined
    return embeddedPayload[queryName] !== undefined || routePayload?.[queryName] !== undefined
  } catch {
    return undefined
  }
}

function sectionQueryEnabled(queryName: string, gate: () => boolean) {
  return () => hasEmbeddedDataForQuery(queryName) ?? gate()
}

/**
 * Build a Blocking + Deferred query pair for one section. Both variants share
 * the same `queryFn`/`queryDeps`; gating is done via `enabled` so that the
 * cookie-derived first-visible section runs as Blocking and the rest run as
 * Deferred.
 */
function sectionQueryPair<Id extends string, Base extends string>(id: Id, baseName: Base) {
  const blockingQueryName = `${baseName}Blocking` as const
  const deferredQueryName = `${baseName}Deferred` as const
  return [
    {
      queryName: blockingQueryName,
      queryDeps: sectionQueryDeps(id),
      queryFn: sectionQueryFn,
      type: QueryRouteQueryType.Blocking,
      enabled: sectionQueryEnabled(blockingQueryName, isBlockingInboxSection(id, getAvailableInboxSectionIds)),
    },
    {
      queryName: deferredQueryName,
      queryDeps: sectionQueryDeps(id),
      queryFn: sectionQueryFn,
      type: QueryRouteQueryType.Deferred,
      enabled: sectionQueryEnabled(deferredQueryName, isDeferredInboxSection(id, getAvailableInboxSectionIds)),
    },
  ] as const
}

/**
 * Adding a new section requires adding an entry here and a matching entry
 * in `inboxSections` (`constants/inbox-sections.ts`).
 */
const INBOX_SECTION_DEFINITIONS = [
  {id: 'review-requested', base: 'sectionReviewRequested'},
  {
    id: 'team-review-requested',
    base: 'sectionTeamReviewRequested',
  },
  {id: 'your-drafts', base: 'sectionYourDrafts'},
  {id: 'waiting-for-review', base: 'sectionWaitingForReview'},
  {id: 'needs-action', base: 'sectionNeedsAction'},
  {id: 'ready-to-merge', base: 'sectionReadyToMerge'},
  {id: 'merge-queue', base: 'sectionMergeQueue'},
] as const satisfies ReadonlyArray<{
  id: string
  base: string
}>

type InboxSectionDefinition = (typeof INBOX_SECTION_DEFINITIONS)[number]

export function getAvailableInboxSectionIds() {
  return INBOX_SECTION_DEFINITIONS.map(section => section.id)
}

export function isInboxSectionId(id: string): id is InboxSectionId {
  return id in INBOX_SECTION_QUERY_NAME_PAIRS
}

/**
 * Per-section Blocking + Deferred query name pairs, derived from
 * `INBOX_SECTION_DEFINITIONS`.
 */
const INBOX_SECTION_QUERY_NAME_PAIRS = Object.fromEntries(
  INBOX_SECTION_DEFINITIONS.map(s => [s.id, {blocking: `${s.base}Blocking`, deferred: `${s.base}Deferred`}]),
) as {
  [Def in InboxSectionDefinition as Def['id']]: {
    blocking: `${Def['base']}Blocking`
    deferred: `${Def['base']}Deferred`
  }
}

type InboxSectionId = keyof typeof INBOX_SECTION_QUERY_NAME_PAIRS

export type InboxSectionQueryName =
  | (typeof INBOX_SECTION_QUERY_NAME_PAIRS)[InboxSectionId]['blocking']
  | (typeof INBOX_SECTION_QUERY_NAME_PAIRS)[InboxSectionId]['deferred']

/**
 * Returns the queryName a consumer should read for a given section: the
 * Blocking variant for the first visible section and Deferred for the rest.
 */
export function getActiveInboxQueryName(
  sectionId: string,
  firstVisibleSectionId: string | undefined,
): InboxSectionQueryName | null {
  if (!isInboxSectionId(sectionId)) return null
  const pair = INBOX_SECTION_QUERY_NAME_PAIRS[sectionId]
  return firstVisibleSectionId === sectionId ? pair.blocking : pair.deferred
}

export const pullsDashboardInboxRoute = dashboardSurfaceAppBuilder.createQueryRouteConfig(
  PULLS_DASHBOARD_INBOX_ROUTE_ID,
  {
    path: PULLS_INBOX_PATH,
    queries: [
      ...sectionQueryPair('review-requested', 'sectionReviewRequested'),
      ...sectionQueryPair('team-review-requested', 'sectionTeamReviewRequested'),
      ...sectionQueryPair('your-drafts', 'sectionYourDrafts'),
      ...sectionQueryPair('waiting-for-review', 'sectionWaitingForReview'),
      ...sectionQueryPair('needs-action', 'sectionNeedsAction'),
      ...sectionQueryPair('ready-to-merge', 'sectionReadyToMerge'),
      ...sectionQueryPair('merge-queue', 'sectionMergeQueue'),
    ],
  },
)
